import type { OpsUsageOverviewResponse, OpsUsageRequestsResponse } from '@nadoutong/contracts';
import { useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import { opsApiRequest } from './client.ts';

const BASE = '/v1/ops/usage';

export type OpsUsageWindow = 'today' | '7d' | '30d' | 'custom';

export interface OpsUsageOverviewQuery {
  readonly window: OpsUsageWindow;
  readonly from?: string;
  readonly to?: string;
  readonly accountId?: string;
  readonly email?: string;
}

export interface OpsUsageRequestsQuery {
  readonly from?: string;
  readonly to?: string;
  readonly accountId?: string;
  readonly email?: string;
  readonly requestId?: string;
  readonly apiKeyId?: string;
  readonly status?: 'success' | 'error';
  readonly model?: string;
  readonly upstreamId?: string;
  readonly limit: number;
  readonly offset: number;
}

function buildQuery(query: object): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query) as Array<[string, string | number | undefined]>) {
    if (value !== undefined && String(value).length > 0) {
      params.set(key, String(value));
    }
  }
  const qs = params.toString();
  return qs.length > 0 ? `?${qs}` : '';
}

export async function getOpsUsageOverview(
  query: OpsUsageOverviewQuery,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<OpsUsageOverviewResponse> {
  return opsApiRequest<OpsUsageOverviewResponse>(
    `${BASE}/overview${buildQuery(query)}`,
    { signal },
    fetchImpl,
  );
}

export async function getOpsUsageRequests(
  query: OpsUsageRequestsQuery,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<OpsUsageRequestsResponse> {
  return opsApiRequest<OpsUsageRequestsResponse>(
    `${BASE}/requests${buildQuery(query)}`,
    { signal },
    fetchImpl,
  );
}

export function useOpsUsageOverview(
  query: OpsUsageOverviewQuery,
): UseQueryResult<OpsUsageOverviewResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: ['ops-usage', 'overview', query],
    queryFn: ({ signal }) => getOpsUsageOverview(query, fetchImpl, signal),
  });
}

export function useOpsUsageRequests(
  query: OpsUsageRequestsQuery,
): UseQueryResult<OpsUsageRequestsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: ['ops-usage', 'requests', query],
    queryFn: ({ signal }) => getOpsUsageRequests(query, fetchImpl, signal),
  });
}
